// Formatting in the user's language: temperatures with one decimal (as in alarm messages), times in
// the site's time zone, durations and relative times.

export type Lang = 'fr' | 'en';

const tag = (lang: Lang) => (lang === 'fr' ? 'fr-FR' : 'en-GB');

export function formatTemp(c: number | null | undefined, lang: Lang, decimals = 1): string {
  if (c === null || c === undefined) return '—';
  return `${new Intl.NumberFormat(tag(lang), { minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(c)} °C`;
}

export function formatNumber(n: number | null | undefined, lang: Lang, decimals = 0): string {
  if (n === null || n === undefined) return '—';
  return new Intl.NumberFormat(tag(lang), { maximumFractionDigits: decimals }).format(n);
}

/** 7900 cents in EUR → "79,00 €" / "€79.00". */
export function formatMoney(cents: number, currency: string, lang: Lang): string {
  return new Intl.NumberFormat(tag(lang), { style: 'currency', currency: currency.toUpperCase() }).format(cents / 100);
}

const formatters = new Map<string, Intl.DateTimeFormat>();
function dtf(lang: Lang, tz: string | undefined, o: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const k = `${lang}|${tz ?? ''}|${JSON.stringify(o)}`;
  let f = formatters.get(k);
  if (!f) {
    f = new Intl.DateTimeFormat(tag(lang), { ...o, ...(tz ? { timeZone: tz } : {}) });
    formatters.set(k, f);
  }
  return f;
}

/** "28 sept. 14:05" (the date only when not today). */
export function formatDateTime(iso: string | null | undefined, lang: Lang, tz?: string, withSeconds = false): string {
  if (!iso) return '—';
  const d = new Date(iso);
  const time = dtf(lang, tz, { hour: '2-digit', minute: '2-digit', ...(withSeconds ? { second: '2-digit' } : {}),
    hourCycle: 'h23' }).format(d);
  const day = (x: Date) => dtf('en', tz, { year: 'numeric', month: '2-digit', day: '2-digit' }).format(x);
  if (day(d) === day(new Date())) return time;
  const sameYear = dtf('en', tz, { year: 'numeric' }).format(d) === dtf('en', tz, { year: 'numeric' }).format(new Date());
  return `${dtf(lang, tz, { day: 'numeric', month: 'short', ...(sameYear ? {} : { year: 'numeric' }) }).format(d)} ${time}`;
}

/** "14:05". */
export function formatTime(iso: string, lang: Lang, tz?: string): string {
  return dtf(lang, tz, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(new Date(iso));
}

export function formatDate(iso: string | null | undefined, lang: Lang, tz?: string): string {
  if (!iso) return '—';
  return dtf(lang, tz, { day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(iso));
}

/** 90 → "1 min 30 s", 7200 → "2 h", 90000 → "1 d 1 h". */
export function formatDuration(seconds: number, lang: Lang): string {
  const s = Math.max(0, Math.round(seconds));
  const units: [number, string, string][] = [[86_400, 'j', 'd'], [3600, 'h', 'h'], [60, 'min', 'min'], [1, 's', 's']];
  const parts: string[] = [];
  let rest = s;
  for (const [size, fr, en] of units) {
    if (rest >= size && parts.length < 2) {
      parts.push(`${Math.floor(rest / size)} ${lang === 'fr' ? fr : en}`);
      rest %= size;
    }
  }
  return parts.length ? parts.join(' ') : `0 s`;
}

/** "il y a 3 min" / "3 min ago". */
export function formatAgo(iso: string | null | undefined, lang: Lang, now = Date.now()): string {
  if (!iso) return '—';
  const diff = Math.round((new Date(iso).getTime() - now) / 1000);
  const rtf = new Intl.RelativeTimeFormat(tag(lang), { numeric: 'auto', style: 'short' });
  const abs = Math.abs(diff);
  if (abs < 60) return rtf.format(diff, 'second');
  if (abs < 3600) return rtf.format(Math.round(diff / 60), 'minute');
  if (abs < 86_400) return rtf.format(Math.round(diff / 3600), 'hour');
  return rtf.format(Math.round(diff / 86_400), 'day');
}

/** The browser's time zone, for defaults. */
export const browserTimeZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

/** 1536 → "1,5 Kio" / "1.5 KiB". */
export function formatBytes(n: number | null | undefined, lang: Lang): string {
  if (n === null || n === undefined) return '—';
  const units = lang === 'fr' ? ['o', 'Kio', 'Mio', 'Gio'] : ['B', 'KiB', 'MiB', 'GiB'];
  let v = n;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${formatNumber(v, lang, i === 0 ? 0 : 1)} ${units[i]}`;
}

function wallClock(t: number, tz: string): number {
  const p = Object.fromEntries(dtf('en', tz, { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit',
    minute: '2-digit', second: '2-digit', hourCycle: 'h23' }).formatToParts(new Date(t)).map((x) => [x.type, x.value]));
  return Date.UTC(Number(p.year), Number(p.month) - 1, Number(p.day), Number(p.hour), Number(p.minute), Number(p.second));
}

/** The local date (YYYY-MM-DD) of an instant in a time zone. */
export function localDate(d: Date, tz: string): string {
  return new Date(wallClock(d.getTime(), tz)).toISOString().slice(0, 10);
}

/** A local date moved by whole days. */
export function addDays(date: string, days: number): string {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** The instant a local date begins in a time zone (daylight saving time included). */
export function zonedDayStart(date: string, tz: string): Date {
  const midnight = Date.parse(`${date}T00:00:00Z`);
  let t = midnight - (wallClock(midnight, tz) - midnight);
  // Once more with the offset in force at the first guess: they differ on the days the clocks change.
  t = midnight - (wallClock(t, tz) - t);
  return new Date(t);
}

/** "Firefox · Windows", from a user agent (the sessions list). */
export function describeAgent(ua: string | null): string {
  if (!ua) return '—';
  const browser = /Edg\//.test(ua) ? 'Edge' : /Firefox\//.test(ua) ? 'Firefox' : /Chrome\//.test(ua) ? 'Chrome'
    : /Safari\//.test(ua) ? 'Safari' : null;
  const os = /Windows/.test(ua) ? 'Windows' : /Android/.test(ua) ? 'Android' : /iPhone|iPad/.test(ua) ? 'iOS'
    : /Mac OS X/.test(ua) ? 'macOS' : /Linux/.test(ua) ? 'Linux' : null;
  return browser && os ? `${browser} · ${os}` : browser ?? os ?? ua.slice(0, 60);
}

/** A file name from a label: accents dropped, other characters browsers may refuse replaced
 *  ("Réfrigérateur n°2" → "Refrigerateur n-2"); Chromium falls back to "download" otherwise. */
export function fileName(label: string): string {
  return label.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^\w .-]+/g, '-').replace(/\s+/g, ' ').trim()
    || 'export';
}
